"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Button, CloseIcon, type ButtonVariant } from "../../atoms/button/button";
import { Tooltip } from "../../atoms/tooltip/tooltip.client";
import { fireLinkClick, firePageView, LINK_POSITION_BODY } from "../../services/analytics";
import styles from "./cart-side-sheet.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/** CartSideSheet — the dark-mode Cart Side Sheet (FUSION-ECLIPSE 10188:19110).
 *
 * A right-anchored `<dialog>` opened with `showModal()`. Same platform bargain
 * as Modal: the top layer, an inert background, Tab containment, Escape, and
 * focus returned to the opener on close come from the element. What is added
 * here is a scroll-lock for the page behind, the analytics page view for the
 * opening, and the layout — the sheet fills the right edge of the viewport
 * rather than centring like a modal, so the `::backdrop` covers everything to
 * the left of the panel and light dismiss reads a click there as "outside".
 *
 * ⚠ The bare `open` attribute renders a NON-modal dialog: no top layer, no
 * inert background, no `::backdrop`. `showModal()` supplies those. */

/** The dialog's accessible name: an on-screen `eyebrowLabel` announced through
 * `aria-labelledby`, or an `ariaLabel` where a host suppresses the eyebrow. */
type CartSideSheetLabelProps =
  | {
      /** The eyebrow wording, and the name the dialog is announced by. */
      eyebrowLabel: string;
      /** Ignored when `eyebrowLabel` is set — the eyebrow already names it. */
      ariaLabel?: never;
    }
  | {
      eyebrowLabel?: never;
      /** Required with no eyebrow: the dialog's only accessible name. */
      ariaLabel: string;
    };

/**
 * One row in the sheet. `indented` shifts the row in and swaps the label for
 * the smaller add-on ramp — the design's way of drawing an included extra
 * under its parent pack. `price` is displayed verbatim: the value can be a
 * price string ("$42.99/mo."), the word "included", the word "applied" for a
 * promo, or empty for a row that has none. The `label` and `subtitle` are
 * what a screen reader announces the row by; `infoLabel` names the info
 * tooltip button when one is present.
 *
 * `tone: "success"` paints the LABEL green — the frame uses it for
 * `PROMO CODES & OFFERS`, where the wording is the applied offer. The
 * trailing cell (e.g. "applied") stays in the neutral price colour on
 * purpose, so the two signals — the offer name in green, the status word
 * in neutral — stay legible against each other. See `cart-side-sheet.module
 * .css` (`.itemSuccess .itemLabel` only, deliberately not `.itemPrice`).
 * No other tone is published.
 */
export interface CartSideSheetItem {
  /** The row's title — e.g. "MyEntertainment®". */
  label: string;
  /** Detail under the label — e.g. "60+ channels". Omit for none. */
  subtitle?: string;
  /** The trailing cell — e.g. "$42.99/mo.", "included", "applied". */
  price?: string;
  /** Small line under the price — e.g. "(Reg. $29.99/mo.)". Shown only
   *  alongside a `price`; omit for none. */
  priceNote?: string;
  /** Names the info tooltip in reporting; the ⓘ appears when set. */
  infoLabel?: string;
  /** Explanation shown in the DDS Tooltip popover when the reader activates
   * the ⓘ. When present, a full `Tooltip` renders (ARIA APG:
   * `aria-describedby` → `role="tooltip"`, Escape, viewport flip). When
   * only `infoLabel` is set without content, a plain reporting-only button
   * renders instead (no popover, click still fires analytics). */
  tooltipContent?: string;
  /** Draws the row as an included add-on: bullet, tighter type, less prominence. */
  indented?: boolean;
  /** `"success"` paints the LABEL green — for promo/applied-offer lines.
   * Trailing cell stays neutral, so "applied"/"included" reads as status
   * against the coloured offer name. No other tone is published. */
  tone?: "success";
}

/** A glyph drawn before a section heading, naming how the service arrives. */
export type CartSectionIcon = "satellite" | "streaming";

/**
 * One section of the sheet — a heading, optional action-link scalars on the
 * right of the header, and its rows. `heading` is `"YOUR PACKAGE"` /
 * `"EXTRAS"` / `"PROMO CODES & OFFERS"` on the frame; a section with no
 * heading renders its items alone, which is what a one-section empty state
 * uses.
 *
 * The action is three flat scalars rather than a nested object: presence is
 * gated on `actionLabel` (the wording) — supply it to render the action.
 * With `actionUrl` the action navigates as an anchor; without it, it becomes
 * a form-submit button that closes the sheet with `returnValue` set to
 * `section-action:<heading>` for a host `onClose` to act on. `actionLinkName`
 * names the click in reporting. Nothing fires without it.
 *
 * ⚠ There is no click handler prop, deliberately: DDS components accept
 * behaviour IN only through the dialog's own routes.
 */
export interface CartSideSheetSection {
  /** Draws the satellite or streaming glyph before the heading. Decorative,
   *  and drawn only with a `heading`; omit for none. */
  headingIcon?: CartSectionIcon;
  /** The section title, e.g. "YOUR PACKAGE". Omit for a header-less section. */
  heading?: string;
  /** The visible wording on the section action — e.g. "Remove all items". Presence gates the whole action; omit to render no action. */
  actionLabel?: string;
  /** Navigates as an anchor when set; the action becomes a form-submit close when not. */
  actionUrl?: string;
  /** Names the section-action click in reporting. Nothing fires without it. */
  actionLinkName?: string;
  /** The rows. Empty renders nothing — the section is still counted for the divider. */
  items: CartSideSheetItem[];
}

interface CartSideSheetBaseProps {
  /** Opens and closes the dialog; supplying it makes it CONTROLLED, and a
   * CHANGE drives the element rather than every render.
   * ⚠ Escape and the close button still close it and fire `onClose` — clear
   * the state there, or the prop and what is on screen diverge.
   * ⚠ A controlled host MUST lower `open` or unmount the sheet in `onClose`.
   * The sheet re-`showModal()`'s itself whenever `open` is still true after
   * a native close (the mechanism `CartSideSheetContainer` relies on for
   * view swaps); a host that handles `onClose` without either lowering
   * `open` or unmounting will trap the reader in the sheet. */
  open?: boolean;
  /** Opens on first render and then leaves the dialog alone. Ignored when
   * `open` is supplied. */
  defaultOpen?: boolean;
  /** Fires after the dialog has closed, however it closed, and receives which
   * control closed it: one of `CLOSED_BY`, or `""` when nothing submitted. That
   * is the platform's own `returnValue`. */
  onClose?: (closedBy: string) => void;
  /** Read aloud for the close control, which has no wording of its own. */
  closeLabel?: string;
  /**
   * Replaces the ✕ glyph only. The close button's size, shape, behaviour and
   * accessible name are unchanged — the name comes from `closeLabel` through
   * `ariaLabel`, which outranks anything inside the button.
   *
   * ⚠ Pass a DECORATIVE node, unvalidated: an `<svg>` wants
   * `aria-hidden="true"`, and anything focusable nests a control inside a
   * control, which no amount of labelling repairs.
   */
  closeIcon?: React.ReactNode;

  /** The section groups the sheet renders. Ignored when `loading` is true. */
  sections?: CartSideSheetSection[];

  /** Renders the sheet's skeleton state instead of `sections` — a single
   * message row inside the panel, no footer. The dialog frame, eyebrow and
   * close control still render, so the panel paints instantly and the
   * reader sees the glass surface while the caller finishes fetching data.
   * Used by the app's Suspense fallback (chunk still loading) and the
   * container's CMS-loading state. */
  loading?: boolean;

  /** The Subtotal row's label — "Subtotal" on the frame. Empty renders no row. */
  subtotalLabel?: string;
  /** The Subtotal row's amount — "$42.99/mo.*". Verbatim; the star is content. */
  subtotalAmount?: string;
  /** Right-aligned lines under the subtotal — "$98.00 due today". */
  subtotalNotes?: string[];
  /** The one-line note under the subtotal — "Taxes and fees applied at
   *  checkout", or just "Taxes and fees" when `taxesValue` carries the rest. */
  taxesNote?: string;
  /** The rest of the note — "applied at checkout" — run on after `taxesNote`
   *  in the same sentence. Shown only with `taxesNote`. */
  taxesValue?: string;
  /** The long legal blurb under the note. */
  legalNote?: string;

  /** Wording on the filled call to action. Omit it and no button renders. */
  primaryLabel?: string;
  /** Makes the primary CTA a link. Without one it closes the dialog. */
  primaryUrl?: string;
  /** Names the primary CTA's click in reporting. Nothing fires without it. */
  primaryLinkName?: string;
  /** Wording on the light call to action. Omit it and no button renders. */
  secondaryLabel?: string;
  /** Makes the secondary CTA a link. Without one it closes the dialog. */
  secondaryUrl?: string;
  /** Names the secondary CTA's click in reporting. Nothing fires without it. */
  secondaryLinkName?: string;
  /** Names the close control's click in reporting. Nothing fires without it. */
  closeLinkName?: string;
  /** Names an info-tooltip click in reporting. Reused across every row. */
  itemInfoLinkName?: string;

  /** The sheet's own name in reporting — `"cart"`. Unique per page, with no
   * default: it is the switch for the page view and for the CTAs' position
   * and destination, so without it nothing is reported. */
  sheetName?: string;
  /** Overrides the derived `linkPosition` (which is `sidesheet_<sheetName>`)
   * for every CTA the sheet fires from itself — primary, secondary,
   * section action, info ⓘ. Reserved for hosts that need production light-
   * mode parity (`panel_cart`) on the same click stream the shipping cart
   * fires. Omit and the derived value is used. */
  linkPosition?: string;
  /** Set by a host that fires the close-control `link_click` from its own
   *  `onClose` handler (usually to align `linkPosition` with a light-mode
   *  vocabulary the sheet cannot derive). Suppresses the dev warning that
   *  otherwise flags a missing `closeLinkName`. Passing this AND
   *  `closeLinkName` would double-fire; the host must supply exactly one. */
  hostFiresClose?: boolean;
  /** The dialog's own id — what a declarative `<button commandfor command>`
   * points at. */
  id?: string;
  /** Lets a click on the overlay outside the panel close the sheet. Off by
   * default; Escape closes the dialog either way. */
  dismissible?: boolean;
  className?: string;
}

export type CartSideSheetProps = CartSideSheetBaseProps & CartSideSheetLabelProps;

/** What `onClose` is handed, so a host compares against a constant. `ESCAPED`
 * is the empty string because that is what the platform leaves in
 * `returnValue` when nothing submitted. Section actions carry the heading in
 * a `SECTION_ACTION_PREFIX:<heading>` value — see `sectionActionReturnValue`. */
export const CLOSED_BY = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  CLOSE_CONTROL: "close",
  ESCAPED: ""
} as const;

/** Prefix for the section-action `returnValue`, so a host can pattern-match on
 * it in `onClose` without conflicting with the other CLOSED_BY constants. The
 * heading is passed verbatim after the colon — an unnamed section reports
 * `section-action:` (empty tail) rather than an ambiguous bare string. */
export const SECTION_ACTION_PREFIX = "section-action";

/** Builds the `returnValue` a section action's form-submit hands `onClose`. */
export function sectionActionReturnValue(heading?: string): string {
  return `${SECTION_ACTION_PREFIX}:${heading ?? ""}`;
}

/** Internal SLOT discriminator — not a Button variant an author reaches. The
 * name mirrors `ButtonVariant` values because the two happen to share those
 * two literal strings today (`renderCta` passes `"primary"`/`"secondary"` as
 * both the Button variant AND the CTA_CLOSED_BY key), but the type is
 * modelling which SLOT of the sheet a CTA occupies, not which visual
 * treatment it wears. */
export type CartCtaSlot = Extract<ButtonVariant, "primary" | "secondary">;

type ClosedByValue = (typeof CLOSED_BY)[keyof typeof CLOSED_BY];

const CTA_CLOSED_BY: Record<CartCtaSlot, ClosedByValue> = {
  primary: CLOSED_BY.PRIMARY,
  secondary: CLOSED_BY.SECONDARY
};

/** How many sheets/modals are open, so nesting cannot unlock the page while
 * one of them is still open. ⚠ Independent of the Modal's own counter on
 * purpose: sharing state across organisms would couple them at the wrong
 * layer, and every open dialog locks the same page anyway — an inner sheet
 * closing does not need to release a lock the outer modal is still holding.
 * The lock is idempotent through the counter below. */
let openSheetCount = 0;
let restoreOverflow: string | null = null;
let restorePadInline: string | null = null;

/** Stops the page behind the dialog scrolling. ⚠ Inertness does not do this:
 * `showModal()` blocks clicks, focus and selection on the background but NOT
 * wheel or touch scrolling of the root. Released on close rather than on
 * `transitionend`, which never fires under `prefers-reduced-motion` and would
 * lock the page for good. */
function lockPageScroll(): void {
  openSheetCount += 1;
  if (openSheetCount > 1) {
    return;
  }
  const root = document.documentElement;
  restoreOverflow = root.style.overflow;
  restorePadInline = root.style.paddingInlineEnd;

  /* Measured BEFORE the overflow is touched — after it the scrollbar is gone
     and the gap reads 0. Logical, so it stays on the scrollbar's edge in RTL. */
  const gutter = window.innerWidth - root.clientWidth;
  if (gutter > 0) {
    root.style.paddingInlineEnd = `${gutter}px`;
  }
  root.style.overflow = "hidden";
}

function unlockPageScroll(): void {
  openSheetCount = Math.max(0, openSheetCount - 1);
  if (openSheetCount > 0) {
    return;
  }
  document.documentElement.style.overflow = restoreOverflow ?? "";
  document.documentElement.style.paddingInlineEnd = restorePadInline ?? "";
  restoreOverflow = null;
  restorePadInline = null;
}

/** The sheet's virtual path, shared by the page view and the CTAs' destination.
 * Absolute with the trailing slash collapsed — a bare pathname reports
 * `//<position>` on the homepage.
 * A host-supplied `linkPosition` override becomes the path segment (so a
 * container aligning to production's `panel_cart` reports `/panel_cart`,
 * not `/sidesheet_cart`); absent, the derived `sidesheet_<name>` wins. */
function sheetVirtualPath(sheetName?: string, linkPositionOverride?: string): string | undefined {
  const segment = linkPositionOverride ?? (sheetName ? `sidesheet_${sheetName}` : undefined);
  if (!segment || typeof window === "undefined") {
    return undefined;
  }
  const { origin, pathname } = window.location;
  return `${origin}${pathname.replace(/\/+$/, "")}/${segment}`;
}

/** The position dimension, which is the sheet's name and nothing else. */
function sheetLinkPositionFor(sheetName?: string): string | undefined {
  return sheetName ? `sidesheet_${sheetName}` : undefined;
}

/** Reports the sheet as a page view. Only ever called from an effect, so
 * `location` is read on the client at fire time. `linkPosition` +
 * `linkDestination` follow the same override the CTAs ride, so a host
 * aligning to production's `panel_cart` sees the page-view under the
 * same funnel dimension as its click stream. */
function reportOpen(sheetName: string, linkPositionOverride?: string): void {
  firePageView({
    linkPosition: linkPositionOverride ?? sheetLinkPositionFor(sheetName),
    linkDestination: sheetVirtualPath(sheetName, linkPositionOverride)
  });
}

/**
 * Development-only diagnostics, compiled out of production builds.
 *
 * Each entry in `ctaPairs` is a slot the sheet renders. When a label ships
 * without a linkName WITH `sheetName` set, the click fires the page-view
 * but no per-CTA `link_click` — silent under-reporting the `sheetName`
 * guard alone cannot catch.
 */
type WarningInput = {
  sheetName?: string;
  linkNames: (string | undefined)[];
  ctaPairs: { name: string; label?: string; linkName?: string }[];
  eyebrowLabel?: string;
  ariaLabel?: string;
  closeLabel?: string;
  open?: boolean;
  onClose?: (returnValue: string) => void;
  sections?: CartSideSheetSection[];
  loading?: boolean;
};

/* ⚠ THE INESCAPABLE SHEET — the one check here that runs in production too.
   The close ✕ is always drawn on this component (there is no `hideCloseControl`
   equivalent on the frame), but a controlled `open` with no `onClose` still
   makes the sheet unreopenable — see the check below. */

function warnInDevelopment({
  sheetName,
  linkNames,
  ctaPairs,
  eyebrowLabel,
  ariaLabel,
  closeLabel,
  open,
  onClose,
  sections,
  loading
}: WarningInput): void {
  /* Checked here because the compiler cannot: the props union splits on the
     label, not on `open`. */
  if (open !== undefined && typeof onClose !== "function") {
    console.error(
      "[CartSideSheet] `open` is controlled but no `onClose` was passed. A " +
        "native close (Escape, light dismiss, form submit) closes the element " +
        "without a React commit, so `open` stays true and the sheet can never " +
        "be reopened. Pass `onClose` and clear the state in it."
    );
  }

  /* The union forbids supplying NEITHER label; `eyebrowLabel=""` still gets through. */
  const name = eyebrowLabel ?? ariaLabel;
  if (typeof name !== "string" || name.trim().length === 0) {
    console.error(
      "[CartSideSheet] the dialog has no accessible name — `eyebrowLabel` or " +
        "`ariaLabel` is present but empty or blank. A screen reader will " +
        'announce "dialog" and nothing about what it wants.'
    );
  }

  if (
    typeof closeLabel === "string" &&
    closeLabel.trim().length === 0
  ) {
    console.error(
      "[CartSideSheet] `closeLabel` is empty, so the close control has no " +
        'accessible name — it is announced as just "button". Pass real ' +
        "wording, or omit the prop to take the default."
    );
  }

  /* An empty sections array renders a valid but blank sheet. `loading`
     explicitly opts in to a skeleton render, so no warning there. */
  if (!loading && (!Array.isArray(sections) || sections.length === 0)) {
    console.warn(
      "[CartSideSheet] `sections` is empty — the sheet renders its header and " +
        "footer with nothing between them. Set `loading` if this is intentional."
    );
  }

  /* Case A — reporting misconfigured because sheetName is missing. */
  if (!sheetName && linkNames.some(Boolean)) {
    console.warn(
      "[CartSideSheet] a linkName was given without `sheetName`, so the click " +
        "will report from the default body position instead of " +
        "`sidesheet_<name>`, and no sheet page view will fire at all. Set " +
        "`sheetName`."
    );
  }

  /* Case B — reporting silently missing because a live CTA has no linkName.
     Only meaningful once `sheetName` is set: without it, case A already
     covers the misconfiguration. Loading and confirmView shells intentionally
     omit both label + linkName; the guard triggers only when a real label
     ships without a name. */
  if (sheetName) {
    ctaPairs.forEach(({ name, label, linkName }) => {
      if (label && !linkName) {
        console.warn(
          `[CartSideSheet] ${name} label "${label}" ships without a linkName; ` +
            "the click fires no `link_click`. Set the linkName so per-CTA " +
            "reporting matches the page-view."
        );
      }
    });
  }
}

/** Section chevron / bullet, drawn inline so DDS does not grow an icon layer
 * for it. `aria-hidden` because the row's label carries the name. */
function BulletGlyph() {
  return (
    <svg
      aria-hidden="true"
      className={styles.bullet}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="8" fill="currentColor" r="2" />
    </svg>
  );
}

/** Info ⓘ, inline for the same reason. `title` is honoured by screen readers
 * as the accessible name when the button carries no children of its own. */
function InfoGlyph() {
  return (
    <svg
      aria-hidden="true"
      className={styles.info}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="8"
        cy="8"
        fill="none"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1"
      />
      <path
        d="M8 6.5v4.25M8 4.4v.6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.2"
      />
    </svg>
  );
}

/* Section glyphs. Decorative — the heading text names the section.
   ⚠ Not in the frame: 10188:19111 draws its section headings as text alone.
   They follow the shipping cart drawer — see design-conflicts/cart-side-sheet. */
function SectionGlyph({ icon }: { icon: CartSectionIcon }) {
  return icon === "satellite" ? (
    <svg aria-hidden="true" className={styles.sectionGlyph} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.2 8.3a5.6 5.6 0 0 0 7.5 7.5L4.2 8.3Zm3.7 3.8 2.6-2.6M10.5 9.5a1.2 1.2 0 1 0 0-.1M12.6 4.6a3.4 3.4 0 0 1 2.8 2.8M12.8 2.2a5.8 5.8 0 0 1 5 5M6 16.8h5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
    </svg>
  ) : (
    <svg aria-hidden="true" className={styles.sectionGlyph} viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.5 7.6a10.6 10.6 0 0 1 15 0M5.1 10.3a6.9 6.9 0 0 1 9.8 0M7.7 13a3.2 3.2 0 0 1 4.6 0"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
      <circle cx="10" cy="15.6" fill="currentColor" r="1.1" />
    </svg>
  );
}

/** Full cart, drawn inline for the same reason. The eyebrow's own icon; not
 * an authorable slot on the frame. */
function CartGlyph() {
  return (
    <svg
      aria-hidden="true"
      className={styles.cartGlyph}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 3.5h2.5l1.9 9.1a1.5 1.5 0 0 0 1.5 1.2h7.4a1.5 1.5 0 0 0 1.5-1.2l1.3-6.4H5.6"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.4"
      />
      <circle cx="8" cy="17" fill="currentColor" r="1.2" />
      <circle cx="15" cy="17" fill="currentColor" r="1.2" />
    </svg>
  );
}

function CartSideSheetImpl({
  open,
  defaultOpen = false,
  onClose,
  eyebrowLabel,
  ariaLabel,
  closeLabel = "Close",
  closeIcon,
  sections,
  loading = false,
  subtotalLabel,
  subtotalAmount,
  subtotalNotes,
  taxesNote,
  taxesValue,
  legalNote,
  primaryLabel,
  primaryUrl,
  primaryLinkName,
  secondaryLabel,
  secondaryUrl,
  secondaryLinkName,
  closeLinkName,
  itemInfoLinkName,
  sheetName,
  linkPosition,
  hostFiresClose = false,
  id,
  dismissible = false,
  className
}: CartSideSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /* One page view per opening. Reset only when the HOST actually lowers
     `open` — a native close the sheet handles internally (a form-submit
     followed by a view swap) leaves the latch armed, so the reopen fires
     no second page-view. */
  const reportedOpeningRef = useRef(false);
  /* The latest `sheetName` for the observer below, which is created once: a
     declarative open causes no commit, so it would otherwise hold mount's. */
  const sheetNameRef = useRef(sheetName);
  const eyebrowId = `dds-cart-side-sheet-eyebrow-${useId().replace(/:/g, "")}`;
  const isControlled = open !== undefined;
  const classes = [styles.dialog, className].filter(Boolean).join(" ");

  /* Host-supplied `linkPosition` wins when present so the CTAs, info ⓘ
     AND the page-view ride the same funnel dimension as the light-mode
     consumer; the derived `sidesheet_<sheetName>` value stays as the
     default. `linkDestination` follows the same override so a click and
     the page-view report the same virtual path. */
  const sheetLinkPosition = linkPosition ?? sheetLinkPositionFor(sheetName);
  const sheetLinkDestination = sheetVirtualPath(sheetName, linkPosition);
  const linkPositionRef = useRef(linkPosition);
  linkPositionRef.current = linkPosition;

  if (process.env.NODE_ENV !== "production") {
    /* ⚠ Paired-prop guard: `hostFiresClose` + a truthy `closeLinkName`
       together produce TWO `link_click` beacons per close — the Button
       atom fires on `closeLinkName`, and the host fires from its own
       `onClose`. AC2 requires exactly one.
       Truthy check matches the Button atom's own `if (linkName)` gate at
       `atoms/button/button.tsx:315`; an empty-string linkName doesn't
       double-fire (Button skips), so it doesn't warn. */
    if (hostFiresClose && closeLinkName) {
      // eslint-disable-next-line no-console
      console.error(
        "[CartSideSheet] `hostFiresClose` and `closeLinkName` are mutually " +
          "exclusive — supplying both makes the close control fire two " +
          "link_click beacons per close (one from the Button atom, one from " +
          "the host's onClose). Pass exactly one."
      );
    }
    /* Close pair excluded when `hostFiresClose` is set — the host owns the
       fire from its own `onClose` handler (usually for a light-mode-aligned
       linkPosition the sheet cannot derive). Including it would cry wolf
       on every render for the one recommended integration pattern. */
    const ctaPairs = [
      { name: "primary", label: primaryLabel, linkName: primaryLinkName },
      { name: "secondary", label: secondaryLabel, linkName: secondaryLinkName },
      ...(hostFiresClose
        ? []
        : [{ name: "close", label: "Close", linkName: closeLinkName }])
    ];
    warnInDevelopment({
      sheetName,
      linkNames: [primaryLinkName, secondaryLinkName, closeLinkName],
      ctaPairs,
      eyebrowLabel,
      ariaLabel,
      closeLabel,
      open,
      onClose,
      sections,
      loading
    });
  }

  /* Latched, so the effect below and the attribute observer cannot both report. */
  const reportOpeningIfNeeded = useCallback(() => {
    const name = sheetNameRef.current;
    if (reportedOpeningRef.current || !name) {
      return;
    }
    reportedOpeningRef.current = true;
    reportOpen(name, linkPositionRef.current);
  }, []);

  /* The close EVENT rather than a handler per control — Escape and the
     form submit close the dialog by routes no handler sees. React's own
     `onClose` for <dialog> is React 19 and this repo is on 18.3.
     ⚠ `closeAttempted` re-triggers the open effect below when the host
     wants a view swap. Bumping a counter (rather than passing state via
     ref) keeps the reopen decision inside React's commit — by the time
     the effect runs, any `setState` the host queued in `onClose` has
     committed and `open` reflects its intent. */
  const [closeAttempted, setCloseAttempted] = useState(0);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }
    const handleClose = () => {
      onClose?.(dialog.returnValue);
      setCloseAttempted(t => t + 1);
    };
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  /* Reset the page-view latch ONLY when the host actually lowers `open`.
     A native close that the host follows with a view swap (still open=true)
     keeps the latch armed so the reopen fires no second event. */
  useEffect(() => {
    if (isControlled && open === false) {
      reportedOpeningRef.current = false;
    }
  }, [isControlled, open]);

  /* ⚠ The `!dialog.open` guard is load-bearing — `showModal()` THROWS on
     an already-open dialog, and React runs effects twice on mount in dev.
     `close(ESCAPED)` rather than bare — `returnValue` keeps the PREVIOUS
     opening's value and would report an abandon as a confirm.
     ⚠ Clearing `returnValue` BEFORE `showModal()` matters — native
     <dialog> preserves the previous closing's returnValue across reopens,
     so an Escape on the second opening would fire the close event with
     the STALE value (e.g. a section-action string) and the consumer would
     route it as that action.
     ⚠ `closeAttempted` in the dep array is what makes the view-swap path
     work: when the host leaves `open` still true after a native close,
     this effect re-runs and calls `showModal()` again. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !isControlled) {
      return;
    }
    if (open && !dialog.open) {
      dialog.returnValue = "";
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close(CLOSED_BY.ESCAPED);
    }
  }, [open, isControlled, closeAttempted]);

  /* Uncontrolled: open once, on mount, and never again — the empty dependency
     list is the point. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (isControlled || !defaultOpen || !dialog || dialog.open) {
      return;
    }
    dialog.returnValue = "";
    dialog.showModal();
  }, []);

  /* Scroll-lock and the declarative opening, both driven off the `open`
     ATTRIBUTE — the one signal every open route shares, including a
     `commandfor` open that React never sees. Must stay AFTER the two open
     effects above. The cleanup unlocks so an unmount cannot strand the page. */
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return undefined;
    }

    let locked = false;
    const sync = () => {
      if (dialog.open) {
        if (!locked) {
          locked = true;
          lockPageScroll();
        }
        reportOpeningIfNeeded();
      } else if (locked) {
        locked = false;
        unlockPageScroll();
      }
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(dialog, { attributeFilter: ["open"] });
    dialog.addEventListener("close", sync);

    return () => {
      observer.disconnect();
      dialog.removeEventListener("close", sync);
      if (locked) {
        locked = false;
        unlockPageScroll();
      }
    };
  }, [reportOpeningIfNeeded]);

  /* The page view. No dependency array, deliberately: `sheetName` is in
     neither open effect's dependencies, so a name resolving a render after the
     dialog opened would never report. */
  useEffect(() => {
    sheetNameRef.current = sheetName;
    if (!dialogRef.current?.open) {
      return;
    }
    reportOpeningIfNeeded();
  });

  /* LIGHT-DISMISS. Same two-ended gesture guard as Modal: `event.target ===
   * dialog` for both press and release means the click stayed on the overlay.
   * The dialog is right-anchored and 370px wide, so "the overlay" is the
   * viewport to the LEFT of it. ⚠ Zero padding on `.dialog` is load-bearing
   * — a click just inside the panel's edge would otherwise close the sheet. */
  const pressedOutsidePanelRef = useRef(false);

  const handleDialogMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    pressedOutsidePanelRef.current = event.target === dialogRef.current;
  };

  const handleDialogMouseUp = (event: React.MouseEvent<HTMLDialogElement>) => {
    pressedOutsidePanelRef.current =
      pressedOutsidePanelRef.current && event.target === dialogRef.current;
  };

  const handleDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    const outsideThroughout = pressedOutsidePanelRef.current;
    pressedOutsidePanelRef.current = false;
    if (!dismissible || !outsideThroughout || event.target !== dialogRef.current) {
      return;
    }
    dialogRef.current?.close(CLOSED_BY.ESCAPED);
  };

  /* `linkDestination` overrides Button's `href` default so a click inside the
     sheet reports the sheet's virtual path. A CTA with somewhere to go is a
     link; one without is a submit inside `<form method="dialog">`, the
     platform's own way to close a dialog — it sets `returnValue` for free. */
  const renderCta = (
    label: string,
    variant: CartCtaSlot,
    href?: string,
    linkName?: string
  ) => {
    const button = (
      <Button
        className={styles.action}
        href={href}
        linkDestination={sheetLinkDestination}
        linkName={linkName}
        linkPosition={sheetLinkPosition}
        size="medium"
        type={href ? "button" : "submit"}
        value={CTA_CLOSED_BY[variant]}
        variant={variant}
      >
        {label}
      </Button>
    );

    return href ? (
      button
    ) : (
      <form className={styles.dialogForm} key={variant} method="dialog">
        {button}
      </form>
    );
  };

  /* Info-click AND section-action analytics — both previously handled by
     local `handleInfoClick` / `handleSectionActionClick` helpers, now
     ridden by the DDS Button atom itself via its `linkName` /
     `linkPosition` / `linkDestination` props. Nothing fires without
     `linkName` (info button: from sheet-level `itemInfoLinkName`; section
     action: from `action.linkName`). */

  /* Section action — mirrors renderCta's split: with an href it is a plain
     anchor and navigates; without one it is a submit inside
     <form method="dialog"> that closes the sheet with the section-scoped
     return value, and the host acts on it in onClose. Uses the DDS Button
     atom (tertiary, xSmall) for both shapes so the two forms read the
     same and analytics ride the Button's own linkName pipeline. */
  const renderSectionAction = (
    actionLabel: string,
    actionUrl: string | undefined,
    actionLinkName: string | undefined,
    heading: string | undefined,
    _linkPosition: string | undefined,
    _linkDestination: string | undefined
  ) => {
    const button = (
      <Button
        className={styles.sectionAction}
        href={actionUrl}
        linkDestination={sheetLinkDestination}
        linkName={actionLinkName}
        linkPosition={sheetLinkPosition}
        size="xSmall"
        type={actionUrl ? "button" : "submit"}
        value={actionUrl ? undefined : sectionActionReturnValue(heading)}
        variant="tertiary"
      >
        {actionLabel}
      </Button>
    );
    return actionUrl ? button : (
      <form className={styles.dialogForm} method="dialog">
        {button}
      </form>
    );
  };

  /* One-row skeleton stand-in used when `loading` is true. Kept internal
     so consumers never construct their own — every caller gets the same
     "Loading your cart…" wording and the same shape. Footer is suppressed
     in the render below when loading. */
  const SKELETON_SECTIONS: CartSideSheetSection[] = [
    { items: [{ label: "Loading your cart…" }] }
  ];
  const renderedSections = loading ? SKELETON_SECTIONS : (sections ?? []);

  const hasFooter =
    !loading && (
      Boolean(subtotalLabel) ||
      Boolean(subtotalAmount) ||
      Boolean(taxesNote) ||
      Boolean(legalNote) ||
      Boolean(primaryLabel) ||
      Boolean(secondaryLabel)
    );

  return (
    <dialog
      aria-label={eyebrowLabel ? undefined : ariaLabel}
      aria-labelledby={eyebrowLabel ? eyebrowId : undefined}
      className={classes}
      /* `closedby` is now typed on `DialogHTMLAttributes` (18.3.31+). Lower-
         case is still the attribute name the platform reads; React would drop
         a `closedBy` camel-case prop. */
      closedby={dismissible ? "any" : undefined}
      id={id}
      onClick={handleDialogClick}
      onMouseDown={handleDialogMouseDown}
      onMouseUp={handleDialogMouseUp}
      ref={dialogRef}
    >
      {/* Close row and eyebrow row sit outside the scroll region, as the frame
          draws. The close row's own form carries `method="dialog"` so a click
          submits it, setting returnValue to CLOSE_CONTROL. */}
      <form className={styles.closeRow} method="dialog">
        <Button
          ariaLabel={closeLabel}
          linkDestination={sheetLinkDestination}
          linkName={closeLinkName}
          linkPosition={sheetLinkPosition}
          size="small"
          startIcon={closeIcon ? closeIcon : <CloseIcon />}
          type="submit"
          value={CLOSED_BY.CLOSE_CONTROL}
          variant="tertiary"
        />
      </form>

      {eyebrowLabel ? (
        <div className={styles.eyebrowBlock}>
          <div className={styles.eyebrow}>
            <CartGlyph />
            <h2 className={styles.eyebrowText} id={eyebrowId}>
              {eyebrowLabel}
            </h2>
          </div>
          <hr aria-hidden="true" className={styles.headerDivider} />
        </div>
      ) : null}

      <div className={styles.body}>
        {renderedSections.map((section, sectionIndex) => (
          <section
            aria-label={section.heading}
            className={styles.section}
            key={section.heading ?? `section-${sectionIndex}`}
          >
            {section.heading || section.actionLabel ? (
              <div className={styles.sectionHeader}>
                {section.heading ? (
                  <h3 className={styles.sectionHeading}>
                    {section.headingIcon ? <SectionGlyph icon={section.headingIcon} /> : null}
                    {section.heading}
                  </h3>
                ) : (
                  <span />
                )}
                {section.actionLabel ?
                  renderSectionAction(
                    section.actionLabel,
                    section.actionUrl,
                    section.actionLinkName,
                    section.heading,
                    sheetLinkPosition,
                    sheetLinkDestination
                  ) :
                  null}
              </div>
            ) : null}
            <ul className={styles.items}>
              {section.items.map((item, itemIndex) => {
                const itemClass = [
                  styles.item,
                  item.indented ? styles.itemIndented : "",
                  item.tone === "success" ? styles.itemSuccess : ""
                ].filter(Boolean).join(" ");
                return (
                  <li
                    className={itemClass}
                    key={`${item.label}-${itemIndex}`}
                  >
                    {item.indented ? <BulletGlyph /> : null}
                    <div className={styles.itemText}>
                      <div className={styles.itemLabelRow}>
                        <span className={styles.itemLabel}>{item.label}</span>
                        {item.tooltipContent ? (
                          /* Full DDS Tooltip popover — ARIA APG pattern
                             (aria-describedby → role=tooltip, Escape, viewport
                             flip, keyboard/touch). The molecule owns its own
                             ⓘ trigger + panel.
                             ⚠ Tooltip has no linkName prop of its own, so the
                             wrapper span captures the TRIGGER click and fires
                             `fireLinkClick`. Panel clicks (a link inside the
                             tooltip, e.g.) must NOT fire — the wrapper spans
                             both, so we gate on the closest `<button>` being
                             an actual `<button>` element (the trigger), not
                             an anchor / non-button descendant of the panel. */
                          <span
                            className={styles.tooltipTrigger}
                            onClickCapture={
                              itemInfoLinkName
                                ? (event: React.MouseEvent) => {
                                    const target = event.target as HTMLElement | null;
                                    const clickedButton = target?.closest?.("button");
                                    /* Trigger is the tooltip's only <button>; anything
                                       else (panel content, link, decoration) skips
                                       the fire. */
                                    if (!clickedButton) {
                                      return;
                                    }
                                    fireLinkClick({
                                      linkName: `${itemInfoLinkName}: ${item.label}`,
                                      linkPosition: sheetLinkPosition || LINK_POSITION_BODY,
                                      linkDestination: sheetLinkDestination
                                    });
                                  }
                                : undefined
                            }
                          >
                            <Tooltip
                              content={item.tooltipContent}
                              label={item.infoLabel}
                            />
                          </span>
                        ) : item.infoLabel ? (
                          /* Reporting-only ⓘ — no popover panel, no tooltip
                             content. Uses the DDS Button atom (icon-only,
                             tertiary on-dark, xSmall) so appearance rides
                             the design-system pipeline, and analytics rides
                             the Button's own linkName / linkPosition /
                             linkDestination. Nothing fires without the
                             sheet-level `itemInfoLinkName`. */
                          <Button
                            ariaLabel={item.infoLabel}
                            className={styles.infoButton}
                            linkDestination={sheetLinkDestination}
                            linkName={
                              itemInfoLinkName ?
                                `${itemInfoLinkName}: ${item.label}` :
                                undefined
                            }
                            linkPosition={sheetLinkPosition}
                            size="xSmall"
                            startIcon={<InfoGlyph />}
                            variant="tertiary"
                          />
                        ) : null}
                      </div>
                      {item.subtitle ? (
                        <span className={styles.itemSubtitle}>{item.subtitle}</span>
                      ) : null}
                    </div>
                    {item.price && item.priceNote ? (
                      <span className={styles.itemPriceCell}>
                        <span className={styles.itemPrice}>{item.price}</span>
                        <span className={styles.itemPriceNote}>{item.priceNote}</span>
                      </span>
                    ) : item.price ? (
                      <span className={styles.itemPrice}>{item.price}</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {sectionIndex < renderedSections.length - 1 ? (
              <hr aria-hidden="true" className={styles.sectionDivider} />
            ) : null}
          </section>
        ))}
      </div>

      {hasFooter ? (
        <div className={styles.footer}>
          {subtotalLabel || subtotalAmount ? (
            <div className={styles.subtotalRow}>
              {subtotalLabel ? (
                <span className={styles.subtotalLabel}>{subtotalLabel}</span>
              ) : null}
              {subtotalAmount ? (
                <span className={styles.subtotalAmount}>{subtotalAmount}</span>
              ) : null}
            </div>
          ) : null}
          {(subtotalNotes ?? []).filter(Boolean).map(note => (
            <p className={styles.subtotalNote} key={note}>{note}</p>
          ))}
          {/* One sentence, as the frame draws it (10188:19145) — the value
              runs on after the note, never pushed to the far edge. */}
          {taxesNote && taxesValue ? (
            <p className={styles.taxesNote}>
              {taxesNote} {taxesValue}
            </p>
          ) : taxesNote ? (
            <p className={styles.taxesNote}>{taxesNote}</p>
          ) : null}
          {legalNote ? (
            <p className={styles.legalNote}>{legalNote}</p>
          ) : null}
          {primaryLabel || secondaryLabel ? (
            <div className={styles.buttons}>
              {primaryLabel ?
                renderCta(primaryLabel, "primary", primaryUrl, primaryLinkName) :
                null}
              {secondaryLabel ?
                renderCta(secondaryLabel, "secondary", secondaryUrl, secondaryLinkName) :
                null}
            </div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
export const CartSideSheet = withSafeRender(CartSideSheetImpl, "CartSideSheet");

export default CartSideSheet;
